export interface ClassifiedError {
  title: string;
  message: string;
  isRetryable: boolean;
  statusCode?: number;
}

export function classifyError(error: any): ClassifiedError {
  if (error && error.name === 'AbortError') {
    return {
      title: 'Request timed out',
      message: 'The network request took too long and timed out. Please try again.',
      isRetryable: true,
      statusCode: 408
    };
  }

  const isNetwork = error && (
    error.message === 'Network Error' || 
    error.message === 'network_failure' ||
    error.message?.includes('Failed to fetch') || 
    error.isNetworkError
  );
  if (isNetwork) {
    return {
      title: 'Network connection unavailable',
      message: 'Unable to establish a connection to the server. Please check your internet connection.',
      isRetryable: true
    };
  }

  // Handle server responses with specific statuses
  if (error && error.statusCode) {
    const status = error.statusCode;
    const backendMessage = error.message;

    if (status === 401) {
      return {
        title: 'Authentication required',
        message: 'Your session has expired. Please sign in again.',
        isRetryable: false,
        statusCode: 401
      };
    }

    if (status === 403) {
      return {
        title: 'Permission denied',
        message: backendMessage || 'You do not have permission to perform this action.',
        isRetryable: false,
        statusCode: 403
      };
    }

    if ([400, 409, 413, 415, 422].includes(status)) {
      let filteredMsg = backendMessage || 'Validation failed.';
      // Ensure specific validation reasons are highlighted correctly
      const lowercaseMsg = filteredMsg.toLowerCase();
      if (lowercaseMsg.includes('5 mb') || lowercaseMsg.includes('oversized') || lowercaseMsg.includes('limit')) {
        filteredMsg = 'File exceeds 5 MB';
      } else if (lowercaseMsg.includes('type') || lowercaseMsg.includes('mime') || lowercaseMsg.includes('genuine')) {
        filteredMsg = 'Unsupported file type';
      } else if (lowercaseMsg.includes('signature') || lowercaseMsg.includes('magic')) {
        filteredMsg = 'Invalid file signature';
      } else if (lowercaseMsg.includes('redact') || lowercaseMsg.includes('redaction')) {
        filteredMsg = 'Missing redaction confirmation';
      } else if (lowercaseMsg.includes('duplicate') || lowercaseMsg.includes('already exists')) {
        filteredMsg = 'Duplicate document';
      } else if (lowercaseMsg.includes('rejection reason') || lowercaseMsg.includes('notes') || lowercaseMsg.includes('reason')) {
        filteredMsg = 'Missing rejection reason';
      }

      return {
        title: 'Document rejected',
        message: filteredMsg,
        isRetryable: false,
        statusCode: status
      };
    }

    if (status === 404) {
      return {
        title: 'Document unavailable',
        message: 'The requested document record could not be found.',
        isRetryable: false,
        statusCode: 404
      };
    }

    if (status === 429) {
      return {
        title: 'Please wait before retrying',
        message: 'Too many requests. Please wait before retrying your request.',
        isRetryable: true,
        statusCode: 429
      };
    }

    if (status >= 500) {
      return {
        title: 'Server could not complete the request',
        message: 'A critical internal error occurred on our systems. No internal details have been exposed.',
        isRetryable: true,
        statusCode: status
      };
    }
  }

  // Handle fallback of non-JSON / generic objects
  return {
    title: 'Server could not complete the request',
    message: 'An unexpected error or malformed response occurred. Please try again.',
    isRetryable: true
  };
}
